import sortBy from 'lodash.sortby';

import { LeagueForManagerViewModel, ListManagerOrCaptainOrScorekeeperLeaguesResponse } from '@controllers/ManagerController';
import { ForgotPasswordRequest, ForgotPasswordResponse, GetRegistrationCodeInfoResponse, IsEmailAvailableResponse, IsPhoneNumberAvailableResponse, LeagueForUserViewModel, ListUserLeaguesResponse, ResetPasswordRequest, ResetPasswordResponse, UserRememberMeResponse, UserSigninRequest, UserSigninResponse, UserSignupRequest, UserSignupResponse, ValidateResetPasswordRequest, ValidateResetPasswordResponse, UnsubscribeRequest, UnsubscribeResponse } from '@controllers/UserController';
import { AxiosResponse } from 'axios';
import axios from '../axios';
import { recaptchaToken } from '@frontend/cloud/recaptcha/recaptcha';

export default class UserController {
  public async isEmailAvailable(email: string): Promise<IsEmailAvailableResponse> {
    const token = await recaptchaToken('isEmailAvailable');
    const response = await axios.post('/api/user/isEmailAvailable', { email }, {
      headers: {
        'X-Recaptcha-Token': token,
      },
    });
    return response.data;
  }

  public async isPhoneNumberAvailable(phoneNumber: string): Promise<IsPhoneNumberAvailableResponse> {
    const token = await recaptchaToken('isPhoneNumberAvailable');
    const response = await axios.post('/api/user/isPhoneNumberAvailable', { phoneNumber }, {
      headers: {
        'X-Recaptcha-Token': token,
      },
    });
    return response.data;
  }

  public async getRegistrationCodeInfo(registrationCode: string): Promise<GetRegistrationCodeInfoResponse> {
    const response = await axios.get(`/api/user/registrationCode/${registrationCode}`);
    return response.data;
  }

  public async signup(request: UserSignupRequest): Promise<UserSignupResponse> {
    const token = await recaptchaToken('signup');
    const response = await axios.put('/api/user/signup', request, {
      headers: {
        'X-Recaptcha-Token': token,
      },
    });
    return response.data;
  }

  public async login(request: UserSigninRequest): Promise<AxiosResponse<UserSigninResponse>> {
    const token = await recaptchaToken('login');
    return axios.put('/api/user/login', request, {
      headers: {
        'X-Recaptcha-Token': token,
      },
      validateStatus: (status) => {
        // Reject only if the status code is greater than or equal to 500
        return status < 500;
      },
    });
  }

  public rememberme(rememberMeToken): Promise<AxiosResponse<UserRememberMeResponse>> {
    return axios.get(`/api/user/rememberme`, {
      headers: {
        'X-Remember-Me': rememberMeToken,
      },
    });
  }

  public async forgotPassword(request: ForgotPasswordRequest): Promise<AxiosResponse<ForgotPasswordResponse>> {
    const token = await recaptchaToken('password_reset');
    return axios.put('/api/user/password/forgot', request, {
      headers: {
        'X-Recaptcha-Token': token,
      },
      validateStatus: (status) => {
        // Reject only if the status code is greater than or equal to 500
        return status < 500;
      },
    });
  }

  public validateResetPassword(request: ValidateResetPasswordRequest): Promise<AxiosResponse<ValidateResetPasswordResponse>> {
    return axios.put('/api/user/password/validateReset', request, {
      validateStatus: (status) => {
        // Reject only if the status code is greater than or equal to 500
        return status < 500;
      },
    });
  }

  public resetPassword(request: ResetPasswordRequest): Promise<AxiosResponse<ResetPasswordResponse>> {
    return axios.post('/api/user/password/reset', request, {
      validateStatus: (status) => {
        // Reject only if the status code is greater than or equal to 500
        return status < 500;
      },
    });
  }

  public unsubscribe(request: UnsubscribeRequest): Promise<AxiosResponse<UnsubscribeResponse>> {
    return axios.post('/api/user/unsubscribe', request, {
      validateStatus: (status) => {
        // Reject only if the status code is greater than or equal to 300
        return status < 300;
      },
    });
  }

  public async getUserLeagues(): Promise<LeagueForUserViewModel[]> {
    const response = await axios.get<ListUserLeaguesResponse>(`/api/user/league`);
    return sortBy(response.data.leagues, 'name');
  }

  public async getManagedLeagues(): Promise<LeagueForManagerViewModel[]> {
    const response = await axios.get<ListManagerOrCaptainOrScorekeeperLeaguesResponse>(`/api/manager/league`);
    return sortBy(response.data.leagues, 'name');
  }
}
